import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import { TableColumn } from 'react-data-table-component'
import { LoanLog, LoanLogSearchCriteria } from '@matech/thebigpos-sdk'
import { SelectChangeEvent } from '@mui/material'
import { useIntl } from 'react-intl'
import { getTheme } from '../../config'
import { useAlert } from '../../hooks'
import useSearch from '../../hooks/useSearch'
import usePagination from '../../hooks/usePagination'
import queryKeys from '../../services/queryKeys'
import { TheBigPOSApi } from '../../lib/TheBigPOSClient'
import { formatDate } from '../../services/helper'
import DataTable from '../DataTable'
import LoanLogSearchFilters from './LoanLogSearchFilters'
import LoanLogTypeLabel from './LoanLogTypeLabel'
import LoanLogLevelBadge from './LoanLogLevelBadge'

const theme = getTheme()

type LoanLogListProps = {
	loanId?: string
	title?: string
}

export type LoanLogSearchCriteriaFilters = {
	types?: string[] | string
	levels?: string[] | string
} & LoanLogSearchCriteria

const LoanLogList = ({ loanId, title }: LoanLogListProps) => {
	const { alert } = useAlert()
	const { formatMessage } = useIntl()
	const { dataTable: breakpoint } = theme.breakpoints

	const { searchText, handleSearchChange, handleClearSearchClick } =
		useSearch()
	const [searchCriteria, setSearchCriteria] =
		useState<LoanLogSearchCriteria>({
			searchText,
			loanID: loanId,
		} as LoanLogSearchCriteria)

	const [filters, setFilters] = useState({
		types: undefined,
		levels: undefined,
	} as LoanLogSearchCriteriaFilters)

	const {
		pageNumber,
		pageSize,
		sortBy,
		sortDirection,
		handleSort,
		handleRowsPerPageChange,
		handlePageChange,
	} = usePagination<LoanLog>({
		defaultSortBy: 'createdAt',
		defaultSortDirection: 'desc',
	})

	const {
		isFetching,
		isRefetching,
		isError,
		data: result,
		refetch,
	} = useQuery({
		queryKey: [
			queryKeys.loanLogs,
			{
				loanId,
				searchCriteria,
				pageNumber,
				pageSize,
				sortBy,
				sortDirection,
			},
		],
		queryFn: () => {
			return TheBigPOSApi.searchLoanLogs(
				loanId ?? '',
				searchCriteria,
				{
					pageNumber,
					pageSize,
					sortBy,
					sortDirection,
				}
			)
		},
	})

	useEffect(() => {
		if (!isError) return
		alert(formatMessage({ id: 'errors.error_loading' }), {
			severity: 'error',
		})
	}, [isError, alert, formatMessage])

	useEffect(() => {
		setSearchCriteria((prev) => ({ ...prev, searchText }))
	}, [searchText])

	const handleFilterChange = (
		e: SelectChangeEvent<string | string[]>,
		field: string
	) => {
		setFilters((prev) => ({
			...prev,
			[field]: e.target.value,
		}))
	}

	// Helper function to convert filter values to backend format
	const convertFilterToBackendFormat = (
		filterValue: string | string[] | undefined
	): string[] | undefined => {
		if (filterValue === 'all' || !filterValue) {
			return undefined
		}
		return Array.isArray(filterValue) ? filterValue : [filterValue]
	}

	// Memoized cell renderers to prevent unnecessary re-renders
	const renderTypeCell = useCallback(
		(row: LoanLog) => <LoanLogTypeLabel type={row.type} />,
		[]
	)

	const renderLevelCell = useCallback(
		(row: LoanLog) => <LoanLogLevelBadge level={row.level} />,
		[]
	)

	const columns: TableColumn<LoanLog>[] = useMemo(
		() => [
			{
				id: 'message',
				name: formatMessage({ id: 'global.message' }),
				sortField: 'message',
				selector: (row: LoanLog) => row.message,
				wrap: true,
				grow: 2,
				sortable: true,
			},
			{
				id: 'Type',
				name: formatMessage({ id: 'global.type' }),
				sortField: 'Type',
				selector: (row: LoanLog) => row.type,
				cell: renderTypeCell,
				sortable: true,
			},
			{
				id: 'Level',
				name: formatMessage({ id: 'global.level' }),
				sortField: 'Level',
				selector: (row: LoanLog) => row.level,
				cell: renderLevelCell,
				sortable: true,
			},
			{
				id: 'createdAt',
				name: formatMessage({ id: 'global.created' }),
				sortField: 'createdAt',
				selector: (row: LoanLog) => row.createdAt,
				cell: (row) => formatDate(row.createdAt, true),
				sortable: true,
				hide: breakpoint,
			},
		],
		[formatMessage, breakpoint, renderTypeCell, renderLevelCell]
	)

	useEffect(() => {
		const backendFilters: Record<string, unknown> = {}

		// Use helper function to convert filter values to backend format
		backendFilters.types = convertFilterToBackendFormat(filters.types)
		backendFilters.levels = convertFilterToBackendFormat(
			filters.levels
		)

		setSearchCriteria((prev) => ({ ...prev, ...backendFilters }))
	}, [filters])

	return (
		<DataTable<LoanLog>
			data={result?.data?.rows || []}
			columns={columns}
			progressPending={isFetching}
			fixedHeader
			onSearchChange={handleSearchChange}
			onSort={handleSort}
			searchText={searchText}
			onClearSearchClick={handleClearSearchClick}
			defaultSortFieldId="createdAt"
			defaultSortAsc={sortDirection === 'asc'}
			onChangePage={handlePageChange}
			onChangeRowsPerPage={handleRowsPerPageChange}
			pagination
			paginationTotalRows={result?.data?.count}
			paginationServer
			title={title}
			onRefreshClick={refetch}
			refreshing={isRefetching}
			searchFilters={
				<LoanLogSearchFilters
					handleFilterChange={handleFilterChange}
					searchCriteria={filters}
				/>
			}
		/>
	)
}

export default LoanLogList
